/* eslint-disable no-unused-vars */
import { ReferenceDataStatus } from "@/lib/interfaces and Enums/ENUMReferenceDataStatus";
import { logClientError } from "@/lib/errorLoggingACTIONS";
import { fetchRequestWrapper } from "@/lib/utilities";

// LQC-2254 - one fetch-once loader shared by every reference-data store (portfolios,
// bond securities). Owns the status transitions so both stores behave identically:
// the endpoint is hit the first time a widget asks for it, and any later widget
// instance reuses what is already in the store.

export const REFERENCE_DATA_ERROR_MESSAGE =
  "Could not load reference data. Try again.";

// The payload did not look like the agreed contract - treated as a failure rather
// than an empty list, so the widget never offers an empty search over bad data.
export const REFERENCE_DATA_BAD_SHAPE_MESSAGE =
  "Reference data came back in an unexpected shape.";

export interface ILoadReferenceDataOnceParams<T> {
  url: string;
  // Identifies the caller in the client error log, e.g. "portfoliosStoreACTIONS".
  source: string;
  getAccessTokenSilently: Function;
  readStatus: () => ReferenceDataStatus;
  setStatus: (status: ReferenceDataStatus) => void;
  setItems: (items: T[]) => void;
  setError: (message: string) => void;
  // Pull the array out of the response envelope. Return null for a shape we do not
  // recognise so a malformed payload is an error, not a silent empty list.
  extractItems: (response: any) => T[] | null;
  forceReload?: boolean;
}

// True when the endpoint still needs calling. Loading and Loaded both mean someone
// has already asked - a second widget instance must not refire the request.
export function shouldFetchReferenceData(
  status: ReferenceDataStatus,
  forceReload = false,
): boolean {
  if (forceReload) return true;
  return (
    status !== ReferenceDataStatus.Loading &&
    status !== ReferenceDataStatus.Loaded
  );
}

export async function loadReferenceDataOnce<T>({
  url,
  source,
  getAccessTokenSilently,
  readStatus,
  setStatus,
  setItems,
  setError,
  extractItems,
  forceReload = false,
}: ILoadReferenceDataOnceParams<T>): Promise<void> {
  if (!shouldFetchReferenceData(readStatus(), forceReload)) return;

  setStatus(ReferenceDataStatus.Loading);
  try {
    const response = await fetchRequestWrapper({ url }, getAccessTokenSilently);
    const items = extractItems(response);
    if (items === null) {
      console.warn(
        `[referenceData] ${url} returned an unexpected shape.`,
        response,
      );
      setError(REFERENCE_DATA_BAD_SHAPE_MESSAGE);
      return;
    }
    setItems(items);
  } catch (error) {
    setError(REFERENCE_DATA_ERROR_MESSAGE);
    logClientError(source, error, getAccessTokenSilently);
  }
}
